import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Bookmark, 
  Calculator, 
  Briefcase, 
  User, 
  ArrowLeft, 
  Menu,
  Heart,
  Search
} from 'lucide-react';
import { FilterState, Property, UserProfile } from '../../types';
import { MobileFiltersSheet } from '../filters/MobileFiltersSheet';
import ToolsSheet from '../ui/ToolsSheet';
import { SearchAutocomplete } from '../SearchAutocomplete';
import { authGate } from '../../lib/authGate';
import { getProjectUrl } from '../../lib/slug';

interface MobileShellProps {
  children: React.ReactNode;
  isGuest: boolean;
  shortlistCount: number;
  filters: FilterState;
  onFilterChange: (newFilters: Partial<FilterState>) => void;
  onSearch: (query: string) => void;
  onLogout: () => void;
  triggerGate?: () => void; // Optional, kept for backward compatibility
  isMobileFiltersOpen: boolean;
  setIsMobileFiltersOpen: (isOpen: boolean) => void;
  userProfile?: UserProfile | null;
  onPropertyClick?: (id: string | number, property?: Property) => void;
}

const MobileShell: React.FC<MobileShellProps> = ({
  children,
  isGuest,
  shortlistCount,
  filters,
  onFilterChange,
  onSearch,
  onLogout,
  triggerGate,
  isMobileFiltersOpen,
  setIsMobileFiltersOpen,
  userProfile,
  onPropertyClick
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isToolsSheetOpen, setIsToolsSheetOpen] = useState(false);

  // Determine page title from route
  const getPageTitle = () => {
    if (location.pathname === '/marketplace' || location.pathname === '/') {
      return 'Marketplace';
    }
    if (location.pathname === '/shortlist') {
      return 'Shortlist';
    }
    if (location.pathname.startsWith('/tools')) {
      return 'Tools';
    }
    if (location.pathname === '/portfolio') {
      return 'Portfolio';
    }
    if (location.pathname === '/account') {
      return 'Account';
    }
    if (location.pathname.startsWith('/property/') || location.pathname.startsWith('/projects/')) {
      return 'Property';
    }
    return 'Off Plan';
  };

  // Check if we should show back button
  const showBackButton = !['/marketplace', '/', '/login', '/onboarding'].includes(location.pathname);

  // Handle protected navigation
  const handleNav = (path: string) => {
    if (isGuest && path !== '/marketplace' && path !== '/') {
      // Use authGate to open the modal (same as desktop)
      authGate.openGate('navigation', {
        path: path,
        action: 'navigate'
      });
      return;
    }
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle search
  const handleSearchSubmit = () => {
    onSearch(searchQuery);
    setIsSearchOpen(false);
    if (location.pathname !== '/marketplace') {
      navigate('/marketplace');
    }
  };

  // Bottom nav items
  const navItems = [
    { 
      icon: Home, 
      label: 'Marketplace', 
      path: '/marketplace',
      guestAllowed: true
    },
    { 
      icon: Bookmark, 
      label: 'Shortlist', 
      path: '/shortlist',
      guestAllowed: false,
      badge: shortlistCount > 0 ? shortlistCount : undefined
    },
    { 
      icon: Calculator, 
      label: 'Tools', 
      path: null, // No direct path - opens sheet instead
      guestAllowed: false,
      isTools: true // Special flag for Tools
    },
    { 
      icon: Briefcase, 
      label: 'Portfolio', 
      path: '/portfolio',
      guestAllowed: false
    },
    { 
      icon: User, 
      label: 'Account', 
      path: '/account',
      guestAllowed: false
    },
  ];

  const isActive = (path: string | null, isTools?: boolean) => {
    if (isTools) {
      return location.pathname.startsWith('/tools') || location.pathname === '/analytics';
    }
    if (path === '/marketplace') {
      return location.pathname === '/marketplace' || location.pathname === '/';
    }
    if (!path) return false;
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBFD] md:hidden">
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 h-16 flex items-center justify-between px-4 safe-area-top">
        {/* Left: Back or Menu */}
        <div className="flex items-center gap-3">
          {showBackButton ? (
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 text-slate-600 hover:text-slate-900 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft size={20} />
            </button>
          ) : (
            <button
              onClick={() => setIsMobileFiltersOpen(true)}
              className="p-2 -ml-2 text-slate-600 hover:text-slate-900 transition-colors"
              aria-label="Menu"
            >
              <Menu size={20} />
            </button>
          )}
          
          {/* Page Title */}
          <h1 className="text-lg font-bold text-slate-900 font-display">
            {getPageTitle()}
          </h1>
        </div>

        {/* Right: Search, Shortlist, Account */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="p-2 text-slate-600 hover:text-slate-900 transition-colors"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
          
          <button
            onClick={() => handleNav('/shortlist')}
            className="relative p-2 text-slate-600 hover:text-slate-900 transition-colors"
            aria-label="Shortlist"
          >
            <Heart size={20} fill={isActive('/shortlist') ? 'currentColor' : 'none'} />
            {shortlistCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {shortlistCount > 99 ? '99+' : shortlistCount}
              </span>
            )}
          </button>

          <button
            onClick={() => handleNav('/account')}
            className="p-2 text-slate-600 hover:text-slate-900 transition-colors"
            aria-label="Account"
          >
            <User size={20} />
          </button>
        </div>
      </header>

      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-[60] bg-white pt-16">
          <div className="px-4 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SearchAutocomplete
                  value={searchQuery}
                  onChange={(query) => {
                    setSearchQuery(query);
                    onSearch(query);
                  }}
                  onSelect={(property) => {
                    if (onPropertyClick) {
                      onPropertyClick(property.id, property);
                    } else {
                      navigate(getProjectUrl(property));
                    }
                    setIsSearchOpen(false);
                  }}
                  placeholder="Search projects, areas, developers..."
                  showSuggestions={true}
                  maxSuggestions={8}
                />
              </div>
              <button
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchQuery('');
                }}
                className="px-4 h-12 rounded-xl bg-slate-900 text-white text-sm font-semibold shrink-0"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Sheet */}
      {location.pathname === '/marketplace' && (
        <MobileFiltersSheet
          open={isMobileFiltersOpen}
          onClose={() => setIsMobileFiltersOpen(false)}
          filters={filters}
          onFilterChange={onFilterChange}
          onClear={() =>
            onFilterChange({
              searchQuery: "",
              locations: [],
              priceMin: "",
              priceMax: "",
              beds: null,
              baths: null,
              type: [],
              developer: "All Developers",
              completion: [],
              sqftMin: "",
              sqftMax: "",
              ppsfMin: "",
              ppsfMax: "",
              priceRange: [0, 50000000],
              sqftRange: [0, 10000],
            })
          }
          onApply={() => {
            // no-op: the list already reacts to filters, but keep hook for future analytics
          }}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 pt-16 pb-20 overflow-y-auto overflow-x-hidden">
        <div className="w-full max-w-full px-4">
          {children}
        </div>
      </main>

      {/* Tools Sheet */}
      <ToolsSheet
        isOpen={isToolsSheetOpen}
        onClose={() => setIsToolsSheetOpen(false)}
        isGuest={isGuest}
        userProfile={userProfile}
        onNavigate={(path) => {
          handleNav(path);
        }}
      />

      {/* Sticky Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-slate-200 safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.isTools);
            const canAccess = item.guestAllowed || !isGuest;

            return (
              <button
                key={item.label}
                onClick={() => {
                  if (item.isTools) {
                    // Tools opens sheet instead of navigating
                    if (canAccess) {
                      setIsToolsSheetOpen(true);
                    } else {
                      authGate.openGate('tools', {
                        path: location.pathname,
                        action: 'open-tools'
                      });
                    }
                  } else if (canAccess) {
                    handleNav(item.path!);
                  } else {
                    // Use authGate for protected navigation
                    authGate.openGate('navigation', {
                      path: item.path || location.pathname,
                      action: item.label.toLowerCase()
                    });
                  }
                }}
                className={`flex flex-col items-center justify-center gap-1 flex-1 min-w-0 ${
                  active 
                    ? 'text-slate-900' 
                    : 'text-slate-400'
                } transition-colors`}
                aria-label={item.label}
              >
                <div className="relative">
                  <Icon 
                    size={22} 
                    fill={active && item.icon === Heart ? 'currentColor' : 'none'}
                    strokeWidth={active ? 2.5 : 2}
                  />
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium ${active ? 'font-semibold' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileShell;
